import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';

// Define types
export type UserType = {
  email: string;
  phone: string;
  isVerified: boolean;
  kycStatus: 'not_started' | 'pending' | 'approved' | 'rejected';
  twoFactorEnabled: boolean;
};

export type TransactionType = {
  id: string;
  date: string;
  amount: string;
  cryptoAmount: string;
  cryptoCurrency: 'USDT' | 'BNB' | 'SOL' | 'USDC';
  status: 'pending' | 'confirmed' | 'completed' | 'failed';
  qrCode?: string;
};

type AppContextType = {
  currentUser: UserType | null;
  isAuthenticated: boolean;
  transactions: TransactionType[];
  currentScreen: string;
  setCurrentScreen: (screen: string) => void;
  login: (email: string, password: string) => void;
  logout: () => void;
  register: (email: string, phone: string, password: string) => void;
  verifyEmail: () => void;
  updateKycStatus: (status: UserType['kycStatus']) => void;
  toggle2FA: () => void;
  createTransaction: (
    amount: string, 
    cryptoAmount: string, 
    cryptoCurrency: TransactionType['cryptoCurrency']
  ) => TransactionType;
  updateTransactionStatus: (id: string, status: TransactionType['status']) => void;
  getCurrentTransaction: () => TransactionType | undefined;
};

const AppContext = createContext<AppContextType | undefined>(undefined);

// Storage keys
const STORAGE_KEYS = {
  USER: 'coinprime_user',
  TRANSACTIONS: 'coinprime_transactions',
  CURRENT_TRANSACTION: 'coinprime_current_transaction',
}

export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Initialize state from localStorage if available
  const [currentUser, setCurrentUser] = useState<UserType | null>(() => {
    const savedUser = localStorage.getItem(STORAGE_KEYS.USER);
    return savedUser ? JSON.parse(savedUser) : null;
  });
  
  const [transactions, setTransactions] = useState<TransactionType[]>(() => {
    const savedTransactions = localStorage.getItem(STORAGE_KEYS.TRANSACTIONS);
    return savedTransactions ? JSON.parse(savedTransactions) : [];
  });
  
  const [currentScreen, setCurrentScreen] = useState(() => {
    return currentUser ? 'dashboard' : 'welcome';
  });
  
  const [currentTransactionId, setCurrentTransactionId] = useState<string | null>(() => {
    const savedId = localStorage.getItem(STORAGE_KEYS.CURRENT_TRANSACTION);
    return savedId || null;
  });

  // Save to localStorage whenever state changes
  useEffect(() => {
    if (currentUser) {
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(currentUser));
    } else {
      localStorage.removeItem(STORAGE_KEYS.USER);
    }
  }, [currentUser]);
  
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.TRANSACTIONS, JSON.stringify(transactions));
  }, [transactions]);
  
  useEffect(() => {
    if (currentTransactionId) {
      localStorage.setItem(STORAGE_KEYS.CURRENT_TRANSACTION, currentTransactionId);
    } else {
      localStorage.removeItem(STORAGE_KEYS.CURRENT_TRANSACTION);
    }
  }, [currentTransactionId]);

  const login = (email: string, password: string) => {
    // In a real app, this would be an API call
    setCurrentUser({
      email,
      phone: '+1234567890', // This would come from the backend
      isVerified: true,
      kycStatus: 'not_started',
      twoFactorEnabled: false,
    });
    setCurrentScreen('dashboard');
  };

  const logout = () => {
    setCurrentUser(null);
    setCurrentScreen('welcome');
    // Do not clear transactions on logout - we want to keep transaction history
  };

  const register = (email: string, phone: string, password: string) => {
    // In a real app, this would be an API call
    setCurrentUser({
      email,
      phone,
      isVerified: false,
      kycStatus: 'not_started',
      twoFactorEnabled: false,
    });
    setCurrentScreen('verify-email');
  };

  const verifyEmail = () => {
    if (currentUser) {
      setCurrentUser({
        ...currentUser,
        isVerified: true,
      });
      setCurrentScreen('dashboard');
    }
  };

  const updateKycStatus = (status: UserType['kycStatus']) => {
    if (currentUser) {
      setCurrentUser({
        ...currentUser,
        kycStatus: status,
      });
    }
  };

  const toggle2FA = () => {
    if (currentUser) {
      setCurrentUser({
        ...currentUser,
        twoFactorEnabled: !currentUser.twoFactorEnabled,
      });
    }
  };

  const createTransaction = (
    amount: string, 
    cryptoAmount: string, 
    cryptoCurrency: TransactionType['cryptoCurrency']
  ): TransactionType => {
    const newTransaction: TransactionType = {
      id: Math.random().toString(36).substring(2, 15),
      date: new Date().toISOString(),
      amount,
      cryptoAmount,
      cryptoCurrency,
      status: 'pending',
    };
    
    setTransactions([newTransaction, ...transactions]);
    setCurrentTransactionId(newTransaction.id);
    return newTransaction;
  };

  const updateTransactionStatus = (id: string, status: TransactionType['status']) => {
    setTransactions(
      transactions.map(tx => 
        tx.id === id 
          ? { 
              ...tx, 
              status, 
              qrCode: status === 'confirmed' ? 'https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=' + tx.id : tx.qrCode 
            } 
          : tx
      )
    );
  };

  const getCurrentTransaction = () => {
    return transactions.find(tx => tx.id === currentTransactionId);
  };

  const contextValue: AppContextType = {
    currentUser,
    isAuthenticated: !!currentUser,
    transactions,
    currentScreen,
    setCurrentScreen,
    login,
    logout,
    register,
    verifyEmail,
    updateKycStatus,
    toggle2FA,
    createTransaction,
    updateTransactionStatus,
    getCurrentTransaction,
  };

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = (): AppContextType => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};
